import { CardSkeleton } from "@/components/atoms/CardSkeleton/CardSkeleton";
import { useOrgRole } from "@/hooks/useOrgRole";
import { ItemCard } from "@/components/molecules/ItemCard";
import { conversationLinkFor } from "@/lib/activityLabels";
import { Button } from "@/components/ui/button";
import { Offer } from "@/schemas/firestore/offer.schema";
import { Plus } from "lucide-react";

interface OfferSectionProps {
  offers: Offer[];
  selectedOffers: string[];
  isLoadingOffers: boolean;
  isGeneratingOffers: boolean;
  onImportOffer: () => void;
  // Offers are backend-write-only (AI generated) -- no manual
  // edit/delete/toggle is available for them.
  onEditOffer?: (id: string) => void;
  onDeleteOffer?: (id: string) => void;
  onToggleOffer?: (id: string) => void;
  onGenerateOffer: () => void;
}

export function OfferSection({
  offers,
  selectedOffers,
  isLoadingOffers,
  isGeneratingOffers,
  onImportOffer,
  onEditOffer,
  onDeleteOffer,
  onToggleOffer,
  onGenerateOffer,
}: OfferSectionProps) {
  // Viewers can read the context but not change it; the backend gates these
  // same actions on min_role=editor, so showing them would only produce 403s.
  const { canEdit } = useOrgRole();

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-1">
          <h3 className="text-lg font-medium">Offers</h3>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onImportOffer}>
              <Plus className="h-4 w-4 mr-2" />
              Import
            </Button>
            <Button
              size="sm"
              className="bg-[#5A5DF6] text-white hover:bg-[#4A4DE6]"
              onClick={onGenerateOffer}
              disabled={isGeneratingOffers}
            >
              {isGeneratingOffers ? "Generating..." : "Generate"}
            </Button>
          </div>
        )}
      </div>

      {isLoadingOffers ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Loading offers...</p>
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : offers.length === 0 ? (
        <p className="text-sm text-muted-foreground">No offers created yet</p>
      ) : (
        <div className="space-y-4">
          {offers.map((offer) =>
            offer.status === "pending" ? (
              <CardSkeleton key={offer.id} />
            ) : (
              <ItemCard
                key={offer.id}
                id={offer.id}
                conversationHref={conversationLinkFor(offer)}
                title={offer.offer_context?.offer_name || "Offer"}
                description={offer.value_messaging?.primary_value_proposition}
                isSelected={selectedOffers.includes(offer.id)}
                onToggle={canEdit ? onToggleOffer : undefined}
                onEdit={canEdit ? onEditOffer : undefined}
                onDelete={canEdit ? onDeleteOffer : undefined}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
}
